// ** Redux
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// ** Helpers
import { createApiCall } from "helpers/createApiCall";

// ############################################################

// Async thunks
export const getLinks = createAsyncThunk(
	"userpanel/links/getLinks",
	createApiCall(async (page, client) => {
		const response = await client.get("/user/allowed-link", {
			params: { page: page || 1 },
		});
		return response.data;
	})
);

export const deleteLinks = createAsyncThunk(
	"userpanel/links/deleteLinks",
	createApiCall(async (ids, client) => {
		await client.delete("/user/allowed-link/multiple", {
			params: { ids: ids.join(",") },
		});
		return { ids };
	})
);

export const addLink = createAsyncThunk(
	"userpanel/links/addLink",
	createApiCall(async ({ link, type }, client) => {
		const response = await client.post("user/allowed-link", {
			link,
			type,
		});

		return response.data;
	})
);

export const changeLink = createAsyncThunk(
	"userpanel/links/changeLink",
	createApiCall(async ({ id, link, type }, client) => {
		const response = await client.put("user/allowed-link", {
			id,
			link,
			type,
		});
		return response.data;
	})
);

// ############################################################

// Initial state
const initialState = {
	listing: {
		loading: false,
		loaded: false,
		success: false,
		error: false,
		data: [],
		pagination: {
			totalpages: 1,
			currentpage: 1,
		},
	},
	editLinks: {
		info: {
			loading: false,
			loaded: false,
			success: false,
			error: false,
			text: [],
			text_raw: "",
			text_edit: [],
			type: false,
		},
	},
};

// ############################################################

// Slice
const userpanelLinksSlice = createSlice({
	name: "userpanel/links",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			// getLinks
			.addCase(getLinks.pending, (state) => {
				state.listing.loading = true;
			})
			.addCase(getLinks.fulfilled, (state, action) => {
				state.listing.loading = false;
				state.listing.loaded = true;
				state.listing.success = true;
				state.listing.error = false;

				state.listing.data = action.payload?.links || [];
				state.listing.pagination = {
					totalpages: action.payload?.pagination?.totalpages || 1,
					currentpage: action.payload?.pagination?.currentpage || 1,
				};
			})
			.addCase(getLinks.rejected, (state) => {
				state.listing = {
					...initialState.listing,
					loading: false,
					loaded: true,
					error: true,
				};
			})

			// deleteLinks
			.addCase(deleteLinks.pending, (state) => {
				state.listing.loading = true;
			})
			.addCase(deleteLinks.fulfilled, (state, action) => {
				state.listing.loading = false;
				state.listing.loaded = true;
				state.listing.success = true;
				state.listing.error = false;
				state.listing.data = state.listing.data.filter(
					({ id }) => !action.payload.ids.includes(id)
				);
			})
			.addCase(deleteLinks.rejected, (state) => {
				state.listing.loading = false;
				state.listing.loaded = true;
				state.listing.error = true;
			})

			// addLink
			.addCase(addLink.pending, (state) => {
				state.editLinks.info.loading = true;
				state.editLinks.info.loaded = false;
				state.editLinks.info.success = false;
				state.editLinks.info.error = false;
			})
			.addCase(addLink.fulfilled, (state, action) => {
				state.editLinks.info.loading = false;
				state.editLinks.info.loaded = true;
				state.editLinks.info.success = true;
				state.editLinks.info.error = false;

				if (action.payload) {
					if (action.payload.data) {
						state.listing.data = [
							...state.listing.data,
							action.payload.data,
						];
					} else {
						state.listing.data = [
							...state.listing.data,
							action.payload,
						];
					}
				}
			})
			.addCase(addLink.rejected, (state) => {
				state.editLinks.info.loading = false;
				state.editLinks.info.loaded = true;
				state.editLinks.info.success = false;
				state.editLinks.info.error = true;
			})

			// changeLink
			.addCase(changeLink.pending, (state) => {
				state.editLinks.info.loading = true;
				state.editLinks.info.loaded = false;
				state.editLinks.info.success = false;
				state.editLinks.info.error = false;
			})
			.addCase(changeLink.fulfilled, (state, action) => {
				state.editLinks.info.loading = false;
				state.editLinks.info.loaded = true;
				state.editLinks.info.success = true;
				state.editLinks.info.error = false;

				if (action.payload) {
					const updatedLink = action.payload.data || action.payload;
					const linkId = updatedLink.id;

					state.listing.data = state.listing.data.map((link) =>
						link.id === linkId ? updatedLink : link
					);
				}
			})
			.addCase(changeLink.rejected, (state) => {
				state.editLinks.info.loading = false;
				state.editLinks.info.loaded = true;
				state.editLinks.info.success = false;
				state.editLinks.info.error = true;
			});
	},
});

// ############################################################

export default userpanelLinksSlice.reducer;

// ############################################################
